import { render } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";
import { api } from "../../api";
import { toastError } from "../../ui/messages";
import { IMG_ROOT } from "../../lib/constants";

// Inferred from the raft router so the shape stays in sync automatically.
type MetadataQueueItem = Awaited<ReturnType<typeof api.metadata.queue>>["items"][number];

function StatusCell({ status }: { status: number }) {
  if (status === 2) {
    return (
      <div style="text-align: center;">
        <span style="font-weight: 800; color: #59B357;">
          <i class="fas fa-check" /> Processed
        </span>
      </div>
    );
  }
  return (
    <div style="text-align: center;">
      <div class="loaderdots" /> {status === 1 ? "Uploaded" : "In queue"}
    </div>
  );
}

function ColorSwatch({ color }: { color: string | null }) {
  if (!color) return <>{color}</>;
  return (
    <>
      <div
        style={`display: inline-block; width: 15px; height: 15px; margin: 0px 7px 0px 0px; position: relative; top: 3px; border-radius: 5px; background: ${color};`}
      />
      {color}
    </>
  );
}

function QueueRow({ item }: { item: MetadataQueueItem }) {
  return (
    <tr>
      <td>
        <img
          src={`${IMG_ROOT}${item.lowResPath}`}
          style="width: 40px; height: 40px; border-radius: 7px; object-fit: cover;"
        />{" "}
        <span style="position: relative; bottom: 12px; left: 9px; font-weight: 600;">
          #{item.artworkId}
        </span>
      </td>
      <td>
        <StatusCell status={item.status} />
      </td>
      <td>{item.added}</td>
      <td>{item.photographer}</td>
      <td>{item.title}</td>
      <td>
        <div style="padding: 0px 20px 0px 0px;">{item.description}</div>
      </td>
      <td>{item.tags}</td>
      <td>{item.locationTags}</td>
      <td>
        <ColorSwatch color={item.primaryColor} />
      </td>
      <td>{item.primaryCategory}</td>
      <td>{item.secondaryCategories}</td>
      <td>{item.isBuilding}</td>
      <td>{item.hasPeople}</td>
      <td>{item.isInsect}</td>
      <td>{item.isIndoor}</td>
    </tr>
  );
}

function MetadataQueue() {
  const [items, setItems] = useState<MetadataQueueItem[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    async function poll() {
      try {
        const response = await api.metadata.queue();
        if (mountedRef.current) {
          setItems(response.items);
          timerRef.current = setTimeout(() => {
            void poll();
          }, 3000);
        }
      } catch (err: unknown) {
        if (mountedRef.current) {
          toastError((err as Error).message);
        }
      }
    }

    void poll();

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <table>
      <tr>
        <td width="200">
          <b>Artwork ID</b>
        </td>
        <td width="180">
          <div style="text-align: center;">
            <b>Status</b>
          </div>
        </td>
        <td width="100">
          <b>When</b>
        </td>
        <td width="200">
          <b>Artist</b>
        </td>
        <td width="150">
          <b>Title</b>
        </td>
        <td>
          <b>Description</b>
        </td>
        <td>
          <b>Tags</b>
        </td>
        <td width="150">
          <b>Location</b>
        </td>
        <td width="100">
          <b>Color</b>
        </td>
        <td width="170">
          <b>Primary category</b>
        </td>
        <td width="270">
          <b>Secondary categories</b>
        </td>
        <td width="90">
          <b>Building</b>
        </td>
        <td width="90">
          <b>People</b>
        </td>
        <td width="90">
          <b>Insect</b>
        </td>
        <td width="90">
          <b>Indoor</b>
        </td>
      </tr>
      {items.map((item) => (
        <QueueRow key={item.artworkId} item={item} />
      ))}
    </table>
  );
}

/**
 * Mount the metadata queue table into #metadataqueue.
 * Replaces the old metadataqueue() function with polling.
 */
export function mountMetadataQueue(): void {
  const container = document.getElementById("metadataqueue");
  if (!container) return;
  render(<MetadataQueue />, container);
}
